import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CalendarIcon, ExternalLinkIcon, NewspaperIcon, Share2Icon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { BLOG_POSTS, BlogPost } from '../data/blog-posts';
import { Navbar } from '../components/Navbar';
import { getCategoryColor, getPostNum, resolveTranslatedField, translateCategory } from '../utils/blog-helpers';
export function NewsStoryPage({
  postId
}: {
  postId: string;
}) {
  const {
    t,
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [postId]);
  // Keep URL hash in sync with current language so shared links preserve it
  useEffect(() => {
    const newHash = `#/news/${postId}?lang=${language}`;
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', newHash);
    }
  }, [language, postId]);
  const post = BLOG_POSTS.find(p => p.id === postId);
  if (!post) {
    return <div className="font-nunito w-full min-h-screen overflow-x-hidden bg-gray-50" data-id="element-4086" data-mp="2jtpl1qpbr8qx">
        <Navbar data-id="element-4087" data-mp="2f396u4dojj3i" />
        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center" data-id="element-4088" data-mp="2f2zi0k4dfhze">
          <h1 className="font-bebas text-4xl text-haa-navy tracking-wider mb-4" data-id="element-4089" data-mp="2a4phdtzvnp6e">
            {isEs ? 'Historia no encontrada' : 'Story Not Found'}
          </h1>
          <p className="font-nunito text-gray-500 mb-8" data-id="element-4090" data-mp="256nxx7yjhnue">
            {isEs ? 'La historia que buscas no existe o ha sido movida.' : "The story you're looking for doesn't exist or has been moved."}
          </p>
          <a href="#" onClick={e => {
          e.preventDefault();
          window.location.hash = '';
        }} className="inline-flex items-center gap-2 bg-haa-blue text-white font-nunito font-bold text-sm px-6 py-3 rounded-full hover:bg-haa-midblue transition-colors" data-id="element-4091" data-mp="1r5tt0zapa50h">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-4092" data-mp="1jtnhqe3osjdb" />
            {isEs ? 'Volver al Inicio' : 'Back to Home'}
          </a>
        </main>
      </div>;
  }
  const postNum = getPostNum(post.id);
  const translatedTitle = resolveTranslatedField(t, postNum, 'title', post.title);
  const translatedDate = resolveTranslatedField(t, postNum, 'date', post.date);
  const translatedFullStory = resolveTranslatedField(t, postNum, 'fullStory', post.fullStory);
  const translatedCategory = translateCategory(t, post.category);
  const handleCopyLink = () => {
    const base = window.location.origin + window.location.pathname;
    const url = `${base}#/news/${postId}?lang=${language}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return <div className="font-nunito w-full min-h-screen overflow-x-hidden bg-gray-50" data-id="element-4093" data-mp="h9oii2fcsqt2">
      <Navbar data-id="element-4094" data-mp="m7yh6n0ae9jf" />
      <main className="pt-24 pb-16" data-id="element-4095" data-mp="m4amhkj3vh34">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4096" data-mp="30dwmeg8em2aa">
          {/* Back link */}
          <motion.div initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.3
        }} data-id="element-4097" data-mp="2vfuwpcgh34pu">
            <a href="#" onClick={e => {
            e.preventDefault();
            window.location.hash = '';
            setTimeout(() => {
              const el = document.getElementById('news');
              if (el) {
                el.scrollIntoView({
                  behavior: 'smooth'
                });
              }
            }, 200);
          }} className="inline-flex items-center gap-2 font-nunito text-haa-blue hover:text-haa-midblue text-sm font-semibold transition-colors mb-8" data-id="element-4098" data-mp="2hny6jdyd74w3">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-4099" data-mp="26nlc31c5tlok" />
              {isEs ? '← Volver a Noticias' : '← Back to News'}
            </a>
          </motion.div>

          {/* Image */}
          {post.image && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="mb-8" data-id="element-4100" data-mp="2gm12f23d4qx0">
              <img src={post.image} alt={translatedTitle} className="w-full max-h-[28rem] object-cover rounded-2xl shadow-lg" style={{
            objectPosition: post.imagePosition || 'center center'
          }} data-id="element-4101" data-mp="2uc4j7djh3sf2" />
            </motion.div>}

          {/* Video Embeds — shown at top when no image */}
          {!post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="mb-8" data-id="element-4102" data-mp="12ebcd4nmyyg3">
              {post.videoEmbeds.map((video, vi) => <div key={vi} className="rounded-2xl overflow-hidden shadow-lg bg-black" data-id="element-4103" data-mp="1lcdx3795i983" data-mpi={vi}>
                  {video.youtubeId ? <div className="relative w-full" style={{
              paddingBottom: '56.25%'
            }} data-id="element-4104" data-mp="1behoon7llsdo">
                      <iframe src={`https://www.youtube.com/embed/${video.youtubeId}`} title={video.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" style={{
                border: 0
              }} data-id="element-4105" data-mp="3w4p94ngut4qy" />
                    </div> : null}
                </div>)}
            </motion.div>}

          {/* Category + Date */}
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} className="flex flex-wrap items-center gap-3 mb-5" data-id="element-4106" data-mp="1zgmlwefxryhs">
            <span className={`font-nunito text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-4107" data-mp="1gj1y7aowr5yr">
              {translatedCategory}
            </span>
            <span className="flex items-center gap-1.5 text-gray-400 text-sm" data-id="element-4108" data-mp="11x4xstngp8ne">
              <CalendarIcon className="w-4 h-4 text-haa-blue" data-id="element-4109" data-mp="xw2xe9ht6qju" />
              <span className="font-nunito" data-id="element-4110" data-mp="1busu3o0puo7o">{translatedDate}</span>
            </span>
          </motion.div>

          {/* Title */}
          <motion.h1 initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="font-bebas text-3xl sm:text-4xl lg:text-5xl text-haa-navy tracking-wider leading-tight mb-8" data-id="element-4111" data-mp="16uob9rcc5hea">
            {translatedTitle}
          </motion.h1>

          {/* Full Story */}
          {translatedFullStory && <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="prose prose-lg max-w-none font-nunito text-gray-600 leading-relaxed whitespace-pre-line mb-10" data-id="element-4112" data-mp="24jpyqoc08mhy">
              {translatedFullStory}
            </motion.div>}

          {/* Story Images Gallery */}
          {post.storyImages && post.storyImages.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.25
        }} className="mb-10 space-y-4" data-id="element-4113" data-mp="6gnslma7gt15">
              {post.storyImages.map((img, idx) => <div key={idx} className="rounded-xl overflow-hidden shadow-md" data-id="element-4114" data-mp="3jv0zcdq5y2gd" data-mpi={idx}>
                  <img src={img} alt={`${translatedTitle} — ${idx + 1}`} className="w-full h-auto object-cover" data-id="element-4115" data-mp="21065o1ww01rj" />
                </div>)}
            </motion.div>}

          {/* Video Embeds — shown after story when post has an image */}
          {post.image && post.videoEmbeds && post.videoEmbeds.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.25
        }} className="mb-10" data-id="element-4116" data-mp="1i1tvccu8olud">
              <h2 className="font-bebas text-2xl text-haa-navy tracking-wider mb-4" data-id="element-4117" data-mp="148ca8nlwn128">
                {isEs ? 'Videos' : 'Videos'}
              </h2>
              <div className="grid grid-cols-1 gap-4" data-id="element-4118" data-mp="ud0uq32o9ptf">
                {post.videoEmbeds.map((video, vi) => <div key={vi} className="rounded-2xl overflow-hidden shadow-lg bg-black" data-id="element-4119" data-mp="2x1l9s776y2sb" data-mpi={vi}>
                    {video.youtubeId ? <div className="relative w-full" style={{
                paddingBottom: '56.25%'
              }} data-id="element-4120" data-mp="35twpoz4oi7u1">
                        <iframe src={`https://www.youtube.com/embed/${video.youtubeId}`} title={video.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" style={{
                  border: 0
                }} data-id="element-4121" data-mp="2e4k1acefhkjm" />
                      </div> : null}
                    <div className="px-4 py-3 bg-gray-900" data-id="element-4122" data-mp="5vcc1nntjrel">
                      <p className="font-nunito text-white/80 text-xs font-semibold truncate" data-id="element-4123" data-mp="9xqrryw6yb1n">
                        {video.label}
                      </p>
                    </div>
                  </div>)}
              </div>
            </motion.div>}

          {/* Coverage Links */}
          {post.coverageLinks && post.coverageLinks.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.25
        }} className="pt-6 border-t border-gray-200 mb-8" data-id="element-4124" data-mp="op94nntr7pgs">
              <div className="flex items-center gap-2 mb-3" data-id="element-4125" data-mp="17jo4ffnw3m5o">
                <NewspaperIcon className="w-4 h-4 text-gray-400" data-id="element-4126" data-mp="1cdit6xicqrbm" />
                <span className="font-nunito text-gray-500 text-sm uppercase tracking-wider font-bold" data-id="element-4127" data-mp="1m87nardrlje1">
                  {t('news.alsoCoveredBy')}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-3" data-id="element-4128" data-mp="1caa3qh6uoarr">
                {post.coverageLinks.map((link, i) => <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white hover:bg-gray-100 font-nunito text-sm font-bold text-haa-midblue hover:text-haa-blue transition-colors border border-gray-200 shadow-sm" data-id="element-4129" data-mp="189gd3g20whv6" data-mpi={i}>
                    {link.label}
                    <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-4130" data-mp="oimiofw9rtxe" />
                  </a>)}
              </div>
            </motion.div>}

          {/* Share Button */}
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.3
        }} className="pt-6 border-t border-gray-200 flex items-center gap-4" data-id="element-4131" data-mp="fksw3wob4enf">
            <button onClick={handleCopyLink} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-nunito font-bold text-sm transition-all duration-200 shadow-sm ${copied ? 'bg-emerald-500 text-white' : 'bg-white border border-gray-200 text-haa-blue hover:bg-haa-blue hover:text-white hover:border-haa-blue'}`} data-id="element-4132" data-mp="355ujdm5afrhj">
              {copied ? <>
                  <CheckIcon className="w-4 h-4" data-id="element-4133" data-mp="2m9kxwds23yni" />
                  {isEs ? '¡Enlace copiado!' : 'Link copied!'}
                </> : <>
                  <Share2Icon className="w-4 h-4" data-id="element-4134" data-mp="1tqnyi81do0wk" />
                  {isEs ? 'Copiar enlace' : 'Copy link'}
                </>}
            </button>
          </motion.div>
        </div>
      </main>
    </div>;
}